import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";

export type PillKind =
  | "cohort"
  | "country"
  | "role"
  | "size"
  | "demoType"
  | "demoer"
  | "selection"
  | "mode"
  | "support"
  | "range"
  | "bucket";

export type FilterPillSpec = {
  label: string;
  kind?: PillKind;
  // Optional clear handler. When present, the pill renders an x button and
  // uses the accent style to read as an interactive selection rather than a
  // page-mirror filter.
  onClear?: () => void;
  // Optional richer detail shown on hover. Use when the label collapses
  // multiple values (e.g. label "2 months", detail ["Jan 2026", "Feb 2026"]).
  // When omitted, the hover tooltip just shows the label.
  detail?: string[];
};

const ICON_FOR: Record<PillKind, string | null> = {
  cohort: "ti-calendar",
  country: "ti-world",
  role: "ti-user",
  size: "ti-building",
  demoType: "ti-presentation",
  demoer: "ti-user-check",
  selection: "ti-target",
  mode: "ti-adjustments",
  support: "ti-shield",
  range: "ti-calendar",
  bucket: "ti-chart-bar",
};

const KIND_HEADING: Record<PillKind, string> = {
  cohort: "Cohort",
  country: "Country",
  role: "Role",
  size: "Company size",
  demoType: "Demo type",
  demoer: "Demoer",
  selection: "Selection",
  mode: "Mode",
  support: "Support roles",
  range: "Range",
  bucket: "Bucket",
};

function basePillStyle(accent: boolean): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: accent ? "rgba(47, 122, 138, 0.10)" : "var(--bg-2)",
    color: accent ? "var(--color-teal, #2F7A8A)" : "var(--fg-2)",
    border: `1px solid ${accent ? "var(--color-teal, #2F7A8A)" : "var(--border-default)"}`,
    borderRadius: 999,
    padding: accent ? "1px 6px 1px 8px" : "1px 8px",
    fontSize: 11,
    fontWeight: accent ? 600 : 500,
    lineHeight: 1.5,
    maxWidth: 240,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };
}

const TIP_WIDTH = 240;
const TIP_MARGIN = 12;

function HoverTip({
  tip, heading, body,
}: {
  tip: { x: number; y: number } | null;
  heading?: string;
  body: ReactNode;
}) {
  if (!tip) return null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const flipLeft = tip.x + TIP_MARGIN + TIP_WIDTH > vw - 8;
  const left = flipLeft ? Math.max(8, tip.x - TIP_MARGIN - TIP_WIDTH) : tip.x + TIP_MARGIN;
  return (
    <div style={{
      position: "fixed", left, top: tip.y + 14,
      width: TIP_WIDTH,
      background: "var(--white, #fff)", border: "1px solid var(--border-default)",
      borderRadius: 7, padding: "8px 10px", fontSize: 11, pointerEvents: "none",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)", color: "var(--fg-1)", zIndex: 30,
      display: "flex", flexDirection: "column", gap: 4, whiteSpace: "normal",
    }}>
      {heading && (
        <div style={{ fontSize: 10, color: "var(--fg-3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
          {heading}
        </div>
      )}
      {body}
    </div>
  );
}

// Kinds that get the rich kind-headed tooltip on hover. Mode/support/selection
// pills are already self-explanatory from their label; a tooltip would just
// repeat what the chip says.
const RICH_TOOLTIP_KINDS: ReadonlySet<PillKind> = new Set([
  "cohort", "country", "role", "size", "demoType", "demoer", "range", "bucket",
]);

function PillChip({ pill }: { pill: FilterPillSpec }) {
  const accent = !!pill.onClear;
  const icon = pill.kind ? ICON_FOR[pill.kind] : null;
  const showRichTip = !!pill.kind && RICH_TOOLTIP_KINDS.has(pill.kind);
  const detailLines = pill.detail && pill.detail.length > 0 ? pill.detail : [pill.label];
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  return (
    <span
      style={{ position: "relative", display: "inline-flex" }}
      onMouseMove={showRichTip ? (e) => setTip({ x: e.clientX, y: e.clientY }) : undefined}
      onMouseLeave={showRichTip ? () => setTip(null) : undefined}
    >
      <span style={basePillStyle(accent)} title={showRichTip ? undefined : pill.label}>
        {icon && <i className={`ti ${icon}`} style={{ fontSize: 11, opacity: 0.7 }} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{pill.label}</span>
        {pill.onClear && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); pill.onClear?.(); }}
            aria-label="Clear filter"
            style={{
              background: "transparent", border: "none", padding: 0, marginLeft: 2,
              cursor: "pointer", color: "inherit", lineHeight: 1, display: "inline-flex",
            }}
          >
            <i className="ti ti-x" style={{ fontSize: 11 }} />
          </button>
        )}
      </span>
      {showRichTip && (
        <HoverTip
          tip={tip}
          heading={pill.kind ? KIND_HEADING[pill.kind] : undefined}
          body={(
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {detailLines.map((d, i) => (
                <div key={i} style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{d}</div>
              ))}
            </div>
          )}
        />
      )}
    </span>
  );
}

function MorePill({ hidden }: { hidden: FilterPillSpec[] }) {
  const [tip, setTip] = useState<{ x: number; y: number } | null>(null);
  if (hidden.length === 0) return null;
  return (
    <span
      onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setTip(null)}
      style={{ position: "relative", display: "inline-flex" }}
    >
      <span style={{ ...basePillStyle(false), cursor: "default" }}>
        <i className="ti ti-dots" style={{ fontSize: 11, opacity: 0.7 }} />
        +{hidden.length} filter{hidden.length === 1 ? "" : "s"}
      </span>
      <HoverTip
        tip={tip}
        heading="Also filtered by"
        body={(
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {hidden.map((p, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {p.kind && ICON_FOR[p.kind] && <i className={`ti ${ICON_FOR[p.kind]}`} style={{ fontSize: 11, opacity: 0.6 }} />}
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</span>
              </div>
            ))}
          </div>
        )}
      />
    </span>
  );
}

export function FilterPills({
  pills, maxVisible = 4,
}: {
  pills: FilterPillSpec[];
  maxVisible?: number;
}) {
  if (pills.length === 0) return null;
  // Always keep clearable (selection) pills visible. They're the most
  // informative bit of context for a chart with a drill-down active.
  const accent = pills.filter((p) => p.onClear);
  const muted = pills.filter((p) => !p.onClear);
  const remainingSlots = Math.max(0, maxVisible - accent.length);
  const visibleMuted = muted.slice(0, remainingSlots);
  const hiddenMuted = muted.slice(remainingSlots);

  return (
    <div style={{
      display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
    }}>
      <i
        className="ti ti-filter"
        aria-hidden="true"
        style={{ fontSize: 13, color: "var(--fg-3)", opacity: 0.7, marginRight: 2 }}
      />
      {accent.map((p, i) => <PillChip key={`a-${i}`} pill={p} />)}
      {visibleMuted.map((p, i) => <PillChip key={`m-${i}`} pill={p} />)}
      <MorePill hidden={hiddenMuted} />
    </div>
  );
}
